import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { environment } from "../../environments/environment";
import type { DigitalCardSettingsResponse } from "./digital-cards.interface";

export async function generateEmployeeHash(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex.substring(0, 8);
}

export function getDigitalCardBaseUrl(): string {
  const url =
    (environment as { digitalCardApiUrl?: string }).digitalCardApiUrl ?? "";
  return url.replace(/\/$/, "");
}

export function buildCardPreviewUrl(
  base: string,
  opts: {
    profileId?: string | null;
    renderId?: string | null;
    domain?: string;
  },
): string {
  if (!base) return "";
  if (opts.profileId && opts.renderId) {
    const params = new URLSearchParams({ previewMode: "1" });
    return `${base}/${opts.profileId}/${opts.renderId}?${params.toString()}`;
  }
  return `${base}/preview`;
}

@Injectable({
  providedIn: "root",
})
export class DigitalCardLinkService {
  private get digitalCardApiUrl(): string {
    return getDigitalCardBaseUrl();
  }

  constructor(private http: HttpClient) {}

  getDigitalCardLink(domain: string): Observable<string> {
    if (!this.digitalCardApiUrl) {
      return of("");
    }
    const url = `${this.digitalCardApiUrl}/api/settings?domain=${encodeURIComponent(domain)}`;
    return this.http.get<DigitalCardSettingsResponse | null>(url).pipe(
      take(1),
      switchMap((res) => {
        if (!res?.id) {
          return of("");
        }
        const base = `${this.digitalCardApiUrl}/${res.id}`;
        if (res.renderId?.trim()) {
          return of(`${base}/${res.renderId.trim()}`);
        }
        return of(base);
      }),
    );
  }
}
